import { useEffect, useRef } from 'react';
import MarkdownContent from './MarkdownContent';
import type { ChatComposerMode } from './ChatComposer';

export interface ChatConversationMessage {
  role: 'user' | 'assistant';
  content: string;
  image_url?: string;
  image_urls?: string[];
}

interface ChatConversationProps {
  messages: ChatConversationMessage[];
  loading?: boolean;
  mode?: ChatComposerMode;
}

const loadingCopyByMode: Record<ChatComposerMode, string> = {
  workflow: 'Building your workflow…',
  research: 'Doing a deep search…',
  portfolio: 'Reading your portfolio…',
};

export default function ChatConversation({
  messages,
  loading = false,
  mode,
}: ChatConversationProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length, loading]);

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 font-display">
      {messages.map((m, i) => (
        <div key={i} className={m.role === 'user' ? 'flex justify-end' : ''}>
          {m.role === 'user' ? (
            <div className="max-w-[80%] rounded-2xl bg-muted px-4 py-3 text-sm text-foreground">
              {m.content}
            </div>
          ) : (
            <div className="w-full">
              <MarkdownContent content={m.content} />
              {(m.image_urls?.length || m.image_url) && (
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {(m.image_urls || (m.image_url ? [m.image_url] : [])).map((url) => (
                    <img
                      key={url}
                      src={url}
                      alt="Generated"
                      className="w-full rounded-xl border border-border object-cover"
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {loading && (
        <p className="text-sm text-muted-foreground">
          {mode ? loadingCopyByMode[mode] : 'Thinking…'}
        </p>
      )}

      <div ref={bottomRef} />
    </div>
  );
}
